import classNames from 'classnames';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { FaFlag, FaRegFlag } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { toast } from "react-toastify";
import { Button, Col, Input, Label, Row, Table } from 'reactstrap';
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, helpers, UserContext } from '../utils';
import { ItemDropdownMenu, PODropdownMenu, POReviewModal, ServiceThreadSpinner, SortHeader } from './';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const FILTER_OPTIONS = {
   late: 1,
   compressed: 2,
   normal: 3
};

export default function PriorityItems(props) {
   const userCtx = useContext(UserContext);
   const { currentUser } = userCtx;
   const [data, setData] = useState([]);
   const [editCopy, setEditCopy] = useState(null);
   const [editIndex, setEditIndex] = useState(null);
   const [modal, setModal] = useState(null);
   const [screenOptions, setScreenOptions] = useState({ filterBy: FILTER_OPTIONS.late, includeNonInventoryItems: false })
   const [sortController, setSortController] = useState({ sortDirection: "asc", sortField: "", });
   const [working, setWorking] = useState(false);
   const editing = !_.isNil(editCopy);

   useEffect(refreshData, [screenOptions, sortController])

   const ExportToExcel = () => {
      let payload = Object.assign({}, screenOptions, sortController);
      api
         .postGetBlob(`Purchasing/ExportPriorityPOs`, payload)
         .then((r) => {
            if (r.data.success === false) {
               if (_.isString(r?.data?.message)) {
                  toast.error(r?.data?.message);
               }
               return;
            }
            if (r.data) {
               const XLSX_FILENAME = `PriorityPurchaseOrders.xlsx`;
               helpers.downloadExport(
                  r.data,
                  XLSX_FILENAME,
                  constants.MIME_TYPES.xlsx
               );
            } else {
               toast.error(r.message || "Error downloading report");
            }
         })
         .catch(api.catchHandler);
   }

   function getValue(field, index, defaultValue = '') {
      const value = editing && editIndex === index
         ? editCopy[field]
         : data[index][field]
      return _.isNil(value)
         ? defaultValue
         : value
   }

   function onSave() {
      setWorking(true);
      const payload = {
         dto: data[editIndex],
         newPromisedDate: editCopy.linePromisedDate,
         NewRequiredDate: editCopy.lineRequiredDate
      }
      api
         .post('Purchasing/UpdatePOLine', payload)
         .then(r => {
            if (r.data.success) {
               toast.success("Changes saved!");
               toggleEdit();
               refreshData();
            } else {
               toast.error(r.data.message);
               setWorking(false);
            }
         })
         .catch(err => {
            api.catchHandler(err)
            setWorking(false);
         })
   }

   const onSortChange = (fieldName, Direction) => {
      setSortController({
         sortDirection: Direction,
         sortField: fieldName,
      });
   };

   function refreshData() {
      document.title = `Priority Purchase Orders`;
      let payload = Object.assign({}, screenOptions, sortController);
      setWorking(true);
      api
         .fetch('Purchasing/PriorityPOs', payload)
         .then(r => {
            if (r.data.success) {
               setData(r.data.message)
            } else {
               toast.error("Error loading Priority PO info");
               console.error(r.data.message)
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorking(false));
   }

   function toggleEdit(index) {
      if (editing) {
         setEditCopy(null);
         setEditIndex(null);
      } else {
         setEditCopy(_.cloneDeep(data[index]))
         setEditIndex(index);
      }
   }

   return (
      <DesktopContainer screenName="Priority Purchase Orders">
         <div className="filter-header mb-3">
            <Row className="text-white justify-content-center">
               <Col xs={2} className="text-end">
                  <div className="h4 pt-1 align-middle">
                     Filter Options:
                  </div>
               </Col>
               <Col xs={4} style={{ marginTop: ".4em" }} className="align-middle">
                  <Input type="radio" name="filterOption" checked={screenOptions.filterBy === FILTER_OPTIONS.late} onChange={() => setScreenOptions({ ...screenOptions, filterBy: FILTER_OPTIONS.late })} className="me-1" />Late
                  <Input type="radio" name="filterOption" checked={screenOptions.filterBy === FILTER_OPTIONS.compressed} onChange={() => setScreenOptions({ ...screenOptions, filterBy: FILTER_OPTIONS.compressed })} className="ms-4 me-1" />Compressed Lead Time
                  <Input type="radio" name="filterOption" checked={screenOptions.filterBy === FILTER_OPTIONS.normal} onChange={() => setScreenOptions({ ...screenOptions, filterBy: FILTER_OPTIONS.normal })} className="ms-4 me-1" />Normal Follow Up
               </Col>
               <Col xs={3} style={{ marginTop: ".3em" }}>
                  <Input
                     type="checkbox"
                     className="my-2"
                     checked={screenOptions.includeNonInventoryItems}
                     onChange={e => setScreenOptions({ ...screenOptions, includeNonInventoryItems: e.target.checked })}
                  />
                  <Label className="pt-1 ms-3">Include Non-Inventory Items?</Label>
               </Col>
            </Row>
         </div>
         <ServiceThreadSpinner on={working}>
            {data && data.length ?
               <>
                  <Row className={"mb-2"}>
                     <Col xs={{ offset: 6, size: 6 }} className="transparent-background">
                        <Button
                           title="Download as .xlsx"
                           className="shredButtonExcel float-end ms-2"
                           onClick={ExportToExcel}
                           disabled={working}
                        >
                           <FontAwesomeIcon icon="file-excel" size="lg" />
                        </Button>
                     </Col>
                  </Row>
                  <Table size="sm" className="data-table">
                     <thead className="table-header">
                        <tr>
                           <th>
                              <SortHeader
                                 displayName={<span>PO<br />Date</span>}
                                 field="DocumentDate"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "DocumentDate"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={<span>Required<br />Date</span>}
                                 field="LineRequiredDate"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "LineRequiredDate"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={<span>Current<br />Promised<br />Date</span>}
                                 field="LinePromisedDate"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "LinePromisedDate"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={<span>PO #</span>}
                                 field="PONumber"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "PONumber"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={<span>Containers</span>}
                                 field="Containers"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "Containers"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={<span>Vendor</span>}
                                 field="VendorWithName"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "VendorWithName"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={<span>Item</span>}
                                 field="ItemWithDescription"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "ItemWithDescription"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={<span>Qty<br />Ordered</span>}
                                 field="OrderQty"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "OrderQty"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={<span>Qty<br />Received</span>}
                                 field="QtyShipped"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "QtyShipped"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={<span>Qty<br />Remaining</span>}
                                 field="QtyRemaining"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "QtyRemaining"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={<span>Days<br />Late</span>}
                                 field="DaysLate"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "DaysLate"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={<span>Lead<br />Time</span>}
                                 field="AvgLeadDays"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "AvgLeadDays"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={<span>Follow<br />Up</span>}
                                 field="NextReviewDate"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "NextReviewDate"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>Review?</th>
                           <th width="3%"></th>
                        </tr>
                     </thead>
                     <tbody>
                        {_.map(data, (datum, idx) => {
                           return (
                              <tr key={idx} className={`tr-${idx % 2 === 0 ? "primary" : "secondary"}`}>
                                 <td className="pt-3">
                                    {dateHelpers.toLongDateString(getValue("documentDate", idx))}
                                 </td>
                                 {editIndex !== idx ?
                                    <td className="pt-3">{dateHelpers.toLongDateString(getValue("lineRequiredDate", idx))}</td> :
                                    <td className="px-0 pt-2">
                                       <Input
                                          type="date"
                                          className={classNames("px-1 w-80", { "readonly-input": editIndex !== idx })}
                                          value={dateHelpers.formatDateForServer(getValue("lineRequiredDate", idx))}
                                          onChange={e => setEditCopy({ ...editCopy, lineRequiredDate: e.target.value })}
                                          readOnly={editIndex !== idx}
                                       />
                                    </td>
                                 }
                                 {editIndex !== idx ?
                                    <td className="pt-3">{dateHelpers.toLongDateString(getValue("linePromisedDate", idx))}</td> :
                                    <td className="px-0 pt-2">
                                       <Input
                                          type="date"
                                          className={classNames("px-1 w-80", { "readonly-input": editIndex !== idx })}
                                          value={dateHelpers.formatDateForServer(getValue("linePromisedDate", idx))}
                                          onChange={e => setEditCopy({ ...editCopy, linePromisedDate: e.target.value })}
                                          readOnly={editIndex !== idx}
                                       />
                                    </td>
                                 }
                                 <PODropdownMenu
                                    poNumber={datum.poNumber}
                                    itemNumber={datum.itemNumber}
                                    attachmentCount={datum.poAttachmentCount}
                                    showAddContainer={currentUser?.isAdmin || currentUser?.isPowerUser || currentUser?.isPurchasingEdit}
                                    showAddCost={currentUser?.isAdmin || currentUser?.isPowerUser || currentUser?.isPurchasingEdit}
                                    containers={datum.containers ?? ""}
                                    callbackRefresh={() => refreshData()}
                                 />
                                 <td className="pt-3">{datum.containers}</td>
                                 <td className="pt-3">
                                    <Link to={constants.ENDPOINTS.vendorDashboard} state={{ label: datum.vendorWithName, value: datum.vendorId }} className="fw-bold shredLink">
                                       {datum.vendorWithName}
                                    </Link>
                                 </td>
                                 <ItemDropdownMenu
                                    itemNumber={datum.itemNumber}
                                    itemDescription={datum.itemWithDescription}
                                    attachmentCount={datum.itemAttachmentCount}
                                 />
                                 <td className="pt-3">{datum.orderQty.toLocaleString()}</td>
                                 <td className="pt-3">{datum.qtyShipped.toLocaleString()}</td>
                                 <td className="pt-3">{datum.qtyRemaining.toLocaleString()}</td>
                                 <td className="pt-3">{datum.daysLate}</td>
                                 <td className="pt-3">{datum.avgLeadDays}</td>
                                 <td className={classNames("pt-3 px-0", { "fw-bold text-danger": Date.parse(getValue("nextReviewDate", idx)) < Date.now() })}>
                                    {dateHelpers.toLongDateString(getValue("nextReviewDate", idx))}
                                 </td>
                                 <td className="pt-3 text-center">
                                    {datum.reviewId === 0
                                       ? <FaRegFlag className="cursor-pointer" color="orangered" onClick={() => setModal(datum)} />
                                       : <FaFlag className="cursor-pointer" color="orangered" onClick={() => setModal(datum)} />}
                                 </td>
                                 <td className="px-0 ">
                                    <Button
                                       className="fw-bold shredLinkButton "
                                       onClick={editing && editIndex === idx ? onSave : () => toggleEdit(idx)}
                                       disabled={working || (editing && editIndex !== idx)}
                                    >
                                       {editing && editIndex === idx ? "Update" : "Edit"}
                                    </Button>
                                    <Button
                                       className={classNames("fw-bold shredLinkButton", { "invisible": !editing || editIndex !== idx })}
                                       onClick={() => toggleEdit(idx)}
                                       disabled={working || (editing && editIndex !== idx)}
                                    >
                                       Cancel
                                    </Button>
                                 </td>
                              </tr>
                           )
                        })}
                     </tbody>
                     <tfoot className="table-footer">
                        <tr>
                           <td colSpan={14} />
                        </tr>
                     </tfoot>
                  </Table>
               </>
               : <div className="h5 text-center">No Priority POs found</div>
            }
         </ServiceThreadSpinner>
         {modal !== null &&
            <POReviewModal
               po={modal}
               toggleCallback={() => setModal(null)}
               refreshCallback={refreshData}
            />
         }
      </DesktopContainer>
   );
}